import Background from "../../../components/Background";
import Topnav from "../../../components/Topnav";
import {
  Box,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { filteredEthnicityList } from "../../../constants/CreateProfileLists";
import { useDispatch } from "react-redux";
import { useState } from "react";
import CopyRightTextMain from "../../../components/CopyRightTextMain";
import { useNavigate } from "react-router-dom";
import { updateFields } from "../../../redux/profileSlice";
import RoundedTextField from "../../../components/RoundedProfileTextField";
import { profileOptionButtonStyles } from "./styles";
import { useEditableProfile } from "../../../hooks/useEditableProfile";
import {
  ETHNICITY_OPTIONS,
  toggleDemographicValue,
} from "../../../constants/demographicOptions";

const EditEthnicity = () => {
  // Router state when opened from the Profile UI, the stored profile on a
  // reload or a pasted link (ECH-387). `RequireProfileLoaded` guarantees one
  // of them exists before this component mounts, so the initialisers below
  // are safe to dereference.
  const { profile: localProfile } = useEditableProfile();
  const demographics = localProfile?.demographics ?? {};
  const [selectedEthnicity, setSelectedEthnicity] = useState(
    demographics.ethnicities ?? [],
  );
  const [customEthnicity, setCustomEthnicity] = useState(
    demographics.ethnicities?.includes("other")
      ? demographics.customEthnicity
      : "",
  );
  const [errorMsg, setErrorMsg] = useState("");
  const [isChecked, setIsChecked] = useState(demographics.ethnicityVisible);
  const hasAnswer = selectedEthnicity.length > 0;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSelect = (ethnicity) => {
    const ethnicities = toggleDemographicValue(
      selectedEthnicity,
      ethnicity.value,
    );
    if (ethnicities.length > 3) {
      setErrorMsg("Maximum 3 selections allowed!");
      return;
    }

    setErrorMsg("");
    setSelectedEthnicity(ethnicities);
    if (!ethnicities.includes("other")) {
      setCustomEthnicity("");
    }
    dispatch(
      updateFields({
        field: "demographics.ethnicities",
        value: ethnicities,
      }),
    );
  };

  const handleCheckboxChange = (event) => {
    if (!hasAnswer) return;
    setIsChecked(event.target.checked);
    dispatch(
      updateFields({
        field: "demographics.ethnicityVisible",
        value: event.target.checked,
      }),
    );
  };

  if (!localProfile) {
    return <div>No profile data found.</div>;
  }

  return (
    <Background>
      <Topnav />
      <Box
        sx={{
          maxWidth: {
            xs: "95% !important",
            sm: "550px !important",
            md: "550px !important",
            lg: "680px !important",
            xl: "740px !important",
          },
        }}
        className="emp-selection-box select-ethnicity-box"
      >
        <Typography
          className="text-primary"
          variant="h5"
          sx={{
            fontWeight: 700,
            fontSize: {
              xs: "18px",
              sm: "20px",
              md: "24px",
              lg: "26px",
              xl: "32px",
            },
            lineHeight: {
              xs: "28px",
              sm: "30px",
              md: "24px",
              lg: "38px",
              xl: "42px",
            },
          }}
        >
          Select Ethnicity
        </Typography>
        <Typography
          variant="body"
          sx={{
            fontWeight: 400,
            fontSize: {
              xs: "12px",
              sm: "13px",
              md: "14px",
              lg: "16px",
            },
            color: "#152134",
          }}
        >
          To gain a deeper understanding of our community and promote
          inclusivity, we invite you to share your ethnicity. If you identify
          with multiple ethnicities, please feel free to select all that apply.
          Your information is confidential and will be used for demographic
          measurement purposes.
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "10px",
            marginTop: "20px",
          }}
        >
          {ETHNICITY_OPTIONS.map((ethnicity) => (
            <Button
              //className="hover-tag-color"
              key={ethnicity.value}
              style={{
                ...profileOptionButtonStyles,
                backgroundColor: selectedEthnicity.includes(ethnicity.value)
                  ? "#7BB9D5"
                  : "white",
                borderColor: selectedEthnicity.includes(ethnicity.value)
                  ? "#7BB9D5"
                  : "#AAB6C8",
              }}
              sx={{
                fontSize: {
                  xs: "12px",
                  sm: "12px",
                  md: "14px",
                },
                padding: {
                  xs: "10px 15px",
                  sm: "10px 15px",
                  md: "15px 20px",
                },
              }}
              onClick={() => handleSelect(ethnicity)}
            >
              {ethnicity.label}
            </Button>
          ))}
        </Box>
        <Box
          sx={{
            width: {
              xs: "100%",
              sm: "100%",
              md: "100%",
              lg: "75%",
            },
            margin: "auto",
          }}
        >
          {selectedEthnicity.includes("other") && (
            <Box
              sx={{
                marginTop: {
                  xs: "20px",
                  sm: "20px",
                  md: "20px",
                  lg: "20px",
                  xl: "30px",
                },
              }}
            >
              <Typography
                sx={{
                  textAlign: "left",
                  fontSize: {
                    xs: "12px",
                    sm: "13px",
                    md: "14px",
                  },
                  fontWeight: 400,
                  marginBottom: "5px",
                  color: "#152134",
                }}
              >
                Please Specify
              </Typography>
              <RoundedTextField
                name="customEthnicity"
                variant="outlined"
                placeholder="Enter here"
                fullWidth
                value={customEthnicity}
                onChange={(e) => {
                  setCustomEthnicity(e.target.value);
                }}
                slotProps={{
                  input: {
                    "aria-label": "custom Ethnicity input",
                  },
                }}
              />
            </Box>
          )}
        </Box>
        {errorMsg && (
          <Typography sx={{ color: "red", marginTop: "10px" }}>
            {errorMsg}
          </Typography>
        )}
        <FormControlLabel
          sx={{
            display: "flex",
            justifyContent: "start",
            textAlign: "left",
            marginLeft: 0,
            marginTop: "12px",
          }}
          control={
            <Checkbox
              color="primary"
              checked={hasAnswer && Boolean(isChecked)}
              disabled={!hasAnswer}
              onChange={handleCheckboxChange}
              sx={{
                margin: 0,
                padding: 0,
                "&:hover": {
                  backgroundColor: "transparent",
                },
                "&.Mui-checked": {
                  color: "#7BB9D5",
                },
              }}
            />
          }
          label={
            <Typography
              component="span"
              sx={{ textAlign: "left", paddingLeft: "8px" }}
              variant="body5"
              className="text-xs text-text-color"
            >
              Display my race/ethnicity on my profile
            </Typography>
          }
        />
        <Button
          variant="contained"
          className="Send-button"
          sx={{
            backgroundColor: selectedEthnicity ? "#2A4876" : "#AAB6C8", // Change color based on input values
            cursor: selectedEthnicity ? "pointer" : "not-allowed", // Disable hover effect when fields are not filled
            color: "#fff",
            marginTop: "20px !important",
            width: {
              xs: "100%",
              sm: "80%",
              md: "80%",
              lg: "370px",
            },
            height: {
              xs: "45px",
              sm: "50px",
              md: "55px",
              lg: "60px",
              xl: "70px",
            },
            fontSize: {
              xs: "15px",
              sm: "15px",
              md: "16px",
              lg: "18px",
            },
            fontWeight: 600,
            borderRadius: "50px",
            textTransform: "capitalize",
          }}
          disabled={
            selectedEthnicity.includes("other")
              ? !customEthnicity
              : selectedEthnicity.length === 0
          }
          onClick={() => {
            if (selectedEthnicity.includes("other")) {
              console.log("custom ethnicity is", customEthnicity);
              let filteredEthnicitiesList = filteredEthnicityList.map(
                (ethnicity) =>
                  ethnicity.toLocaleLowerCase().replace(/[^a-z0-9]/g, ""),
              );
              if (
                filteredEthnicitiesList.includes(
                  customEthnicity.toLocaleLowerCase().replace(/[^a-z0-9]/g, ""),
                )
              ) {
                setErrorMsg(
                  "Invalid entry: Please enter a Ethnicity that is not listed in the options.",
                );
              } else {
                dispatch(
                  updateFields({
                    field: "demographics.customEthnicity",
                    value: customEthnicity,
                  }),
                );
                dispatch(
                  updateFields({
                    field: "demographics.ethnicities",
                    value: selectedEthnicity,
                  }),
                );
                navigate("/profile/edit-profile");
              }
            } else {
              navigate("/profile/edit-profile");
            }
          }}
        >
          Save
        </Button>
        <Typography
          style={{
            cursor: "pointer",
          }}
          onClick={() => {
            dispatch(
              updateFields({
                field: "demographics.ethnicities",
                value: [],
              }),
            );
            navigate("/profile/edit-profile");
          }}
          variant="body2"
          sx={{
            color: "#152134",
            fontSize: "14px",
            marginTop: {
              xs: "10px",
              sm: "10px",
              md: "10px",
              lg: "10px",
              xl: "20px",
            },
          }}
        >
          Prefer not to say
        </Typography>
      </Box>
      <CopyRightTextMain />
    </Background>
  );
};

export default EditEthnicity;
